import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ChartComponent } from '@next/core-lib/chart';
import { SummaryCardComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { AgrNumberPipe, LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';
import { Store } from '@ngrx/store';
import {
  Options,
  SeriesColumnOptions,
  SeriesOptionsType,
  TooltipOptions,
  XAxisOptions,
  YAxisOptions,
  YAxisPlotLinesOptions,
} from 'highcharts';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  BenchmarkData,
  BenchmarkSettings,
  DataViewType,
  FarmerInsightsModuleState,
  FlockDataParameter,
} from 'src/app/farmer-insights/models/farmer-insights.model';
import {
  getBenchmarkCustomerAveragSelectedPeriodData,
  getBenchmarkLastWeeksData,
  getBenchmarkSelectedPeriodData,
  getBenchmarkSettings,
  getBenchmarkTimeSeries,
  getSelectedDataViewType,
  getSelectedKpis,
  getTimeSeries,
} from 'src/app/farmer-insights/store/selectors/farmer-insights.selectors';
import { MIN_FLOCKS_FOR_BENCHMARK } from 'src/app/shared/constants';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';

@Component({
  selector: 'app-data-view-graph-detailed',
  templateUrl: './data-view-graph-detailed.component.html',
  styleUrls: ['./data-view-graph-detailed.component.scss'],
  imports: [
    NgIf,
    NgFor,
    SummaryCardComponent,
    ChartComponent,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DataViewGraphDetailedComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private readonly decimalPipe = inject(AgrNumberPipe);
  private readonly locale = inject(LocaleService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);

  public kpis$: Observable<FlockDataParameter[]> = this.store.select(getSelectedKpis);
  public timeseries$: Observable<DataSeries<number>> = this.store.select(getTimeSeries);
  public benchmarkSettings$: Observable<BenchmarkSettings> =
    this.store.select(getBenchmarkSettings);
  public benchmarkTimeSeries$: Observable<DataSeries<number>> =
    this.store.select(getBenchmarkTimeSeries);
  public benchmarkSelectedPeriodData$: Observable<BenchmarkData> = this.store
    .select(getBenchmarkSelectedPeriodData)
    .pipe(distinctUntilChanged());
  public benchmarkLastWeeksData$: Observable<BenchmarkData> = this.store
    .select(getBenchmarkLastWeeksData)
    .pipe(distinctUntilChanged());
  public benchmarkCustomerAveragSelectedPeriodData$: Observable<BenchmarkData> = this.store
    .select(getBenchmarkCustomerAveragSelectedPeriodData)
    .pipe(distinctUntilChanged());
  public selectedDataViewType$: Observable<DataViewType> =
    this.store.select(getSelectedDataViewType);

  subTimeSeries: Subscription;
  hasAvailableData = false;
  kpiItems: SeriesOptionsType[] = [];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit() {
    this.initGraph();
  }

  ngOnDestroy() {
    this.subTimeSeries?.unsubscribe();
  }

  options: { key: string; data: Highcharts.Options }[] = [];
  data: { key: string; data: SeriesColumnOptions[] }[] = [];
  benchmarksSelectedPeriod: { key: string; data: number }[] = [];
  benchmarksLastWeeks: { key: string; data: number }[] = [];
  benchmarkCustomerAveragSelectedPeriodData: { key: string; data: number }[] = [];

  initGraph() {
    this.subTimeSeries = combineLatest([
      this.timeseries$,
      this.benchmarkSelectedPeriodData$,
      this.benchmarkLastWeeksData$,
      this.benchmarkCustomerAveragSelectedPeriodData$,
      this.selectedDataViewType$,
      this.benchmarkSettings$,
    ]).subscribe(
      ([
        timeSeries,
        benchmarkSelectedPeriod,
        benchmarkLastWeeks,
        benchmarkCustomerAveragSelectedPeriodData,
        dataViewType,
        benchmarkSettings,
      ]: [
        DataSeries<number>,
        BenchmarkData,
        BenchmarkData,
        BenchmarkData,
        DataViewType,
        BenchmarkSettings,
      ]) => {
        this.options = [];
        this.data = [];
        this.benchmarksLastWeeks = [];
        this.benchmarksSelectedPeriod = [];
        this.benchmarkCustomerAveragSelectedPeriodData = [];

        if (
          benchmarkSettings.selectedPeriod &&
          benchmarkSelectedPeriod !== null &&
          benchmarkSelectedPeriod.flocksUsed > MIN_FLOCKS_FOR_BENCHMARK
        ) {
          benchmarkSelectedPeriod.benchmarks.forEach((benchmark) => {
            this.benchmarksSelectedPeriod.push({
              key: benchmark.parameter.name.toString(),
              data: benchmark.value,
            });
          });
        }

        if (
          benchmarkSettings.lastWeeks &&
          benchmarkLastWeeks !== null &&
          benchmarkLastWeeks.flocksUsed > MIN_FLOCKS_FOR_BENCHMARK
        ) {
          benchmarkLastWeeks.benchmarks.forEach((benchmark) => {
            this.benchmarksLastWeeks.push({
              key: benchmark.parameter.name.toString(),
              data: benchmark.value,
            });
          });
        }

        if (
          benchmarkSettings.customerAverageSelectedPeriod &&
          benchmarkCustomerAveragSelectedPeriodData !== null
        ) {
          benchmarkCustomerAveragSelectedPeriodData.benchmarks.forEach((benchmark) => {
            this.benchmarkCustomerAveragSelectedPeriodData.push({
              key: benchmark.parameter.name.toString(),
              data: benchmark.value,
            });
          });
        }

        if (timeSeries === null) {
          this.hasAvailableData = false;
          return;
        } else {
          this.hasAvailableData = true;
        }
        // Process KPI's.
        const kpis = Object.keys(timeSeries.series);

        this.hasAvailableData = timeSeries?.series[kpis[0]]?.length > 0;

        if (this.hasAvailableData) {
          kpis.forEach((kpi) => {
            const chart = this.buildChart(kpi, timeSeries, dataViewType);
            this.data.push({
              key: kpi,
              data: chart.data,
            });
            this.options.push({ key: kpi, data: chart.options });
          });
        }
      },
    );
  }

  private buildChart(
    kpi: string,
    timeSeries: DataSeries<number>,
    dataViewType: DataViewType,
  ): { options: Highcharts.Options; data: SeriesColumnOptions[] } {
    switch (dataViewType) {
      case DataViewType.PerLocation:
        return this.buildPerLocationChart(kpi, timeSeries);
      case DataViewType.PerCycle:
        return this.buildPerCycleChart(kpi, timeSeries);
      case DataViewType.PerCycleBarn:
        return this.buildPerCycleBarnChart(kpi, timeSeries);
      case DataViewType.PerLocationBarn:
        return this.buildPerLocationBarnChart(kpi, timeSeries);
    }
  }

  private buildPerLocationChart(
    kpi: string,
    timeSeries: DataSeries<number>,
  ): { options: Highcharts.Options; data: SeriesColumnOptions[] } {
    const option = this.createChartOptions(kpi, timeSeries, true);
    const values: number[] = [];
    const locationIds: number[] = [];

    this.getKpiSeries(timeSeries, kpi).forEach((seriesItem) => {
      if (seriesItem.properties?.LocationId === undefined) {
        return;
      }

      const locationId = +seriesItem.properties.LocationId;
      if (!locationIds.includes(locationId)) {
        locationIds.push(locationId);
        (option.xAxis as XAxisOptions).categories?.push(
          this.stringifyProperty(seriesItem.properties.LocationName),
        );
      }

      if (
        seriesItem.name !== 'Total' &&
        locationIds.includes(locationId) &&
        seriesItem.values[0].value !== 0
      ) {
        values.push(seriesItem.values[0].value);
      }
    });

    return {
      options: option,
      data: [{ type: 'column', data: values }],
    };
  }

  private buildPerCycleChart(
    kpi: string,
    timeSeries: DataSeries<number>,
  ): { options: Highcharts.Options; data: SeriesColumnOptions[] } {
    const option = this.createChartOptions(kpi, timeSeries);
    const valuesPerCycle: { name: string; value: number }[] = [];

    this.getKpiSeries(timeSeries, kpi).forEach((seriesItem) => {
      if (seriesItem.properties?.LocationId === undefined || !seriesItem.properties?.CycleId) {
        return;
      }

      (option.xAxis as XAxisOptions).categories?.push(
        `${this.locale.translate('report-overview.cycle')} ${this.stringifyProperty(seriesItem.properties.CycleNumber)}`,
      );

      if (seriesItem.name !== 'Total' && seriesItem.values[0].value.toString() !== 'NaN') {
        valuesPerCycle.push({
          name: this.stringifyProperty(seriesItem.properties.LocationName),
          value: seriesItem.values[0].value,
        });
      }
    });

    return {
      options: option,
      data: this.createSeriesByName(valuesPerCycle),
    };
  }

  private buildPerCycleBarnChart(
    kpi: string,
    timeSeries: DataSeries<number>,
  ): { options: Highcharts.Options; data: SeriesColumnOptions[] } {
    const option = this.createChartOptions(kpi, timeSeries);
    const cycleBarnGroups: {
      cycleId: number;
      cycleNumber: number;
      locationName: string;
      barns: { id: number; name: string; value: number }[];
    }[] = [];
    const barns: { id: number; name: string }[] = [];

    this.getKpiSeries(timeSeries, kpi).forEach((seriesItem) => {
      if (
        seriesItem.properties?.LocationId === undefined ||
        seriesItem.properties.CycleId === undefined ||
        seriesItem.properties.BarnId === undefined ||
        seriesItem.name === 'Total' ||
        seriesItem.values[0].value.toString() === 'NaN'
      ) {
        return;
      }

      const cycleId = +seriesItem.properties.CycleId;
      const barnId = +seriesItem.properties.BarnId;
      const barnName = this.getPropertyString(seriesItem.properties.BarnName);
      const locationName = this.getPropertyString(seriesItem.properties.LocationName);
      const existingCycle = cycleBarnGroups.find((group) => group.cycleId === cycleId);
      const barnValue = {
        id: barnId,
        name: barnName,
        value: +seriesItem.values[0].value,
      };

      if (existingCycle) {
        existingCycle.barns.push(barnValue);
      } else {
        cycleBarnGroups.push({
          cycleId,
          cycleNumber: +seriesItem.properties.CycleNumber,
          locationName,
          barns: [barnValue],
        });
      }

      if (!barns.some((barn) => barn.id === barnId)) {
        barns.push({
          id: barnId,
          name: barnName,
        });
      }
    });

    const sortedBarns = [...barns].sort((left, right) => left.id - right.id);
    (option.xAxis as XAxisOptions).categories = cycleBarnGroups.map(
      (cycleBarns) =>
        `${this.locale.translate('report-overview.cycle')} ${cycleBarns.cycleNumber} - ${cycleBarns.locationName}`,
    );

    const data = sortedBarns.map((barn) => ({
      type: 'column' as const,
      name: barn.name,
      data: cycleBarnGroups.map((cycleBarns) => {
        const cycleBarn = cycleBarns.barns.find((existingBarn) => existingBarn.id === barn.id);
        return cycleBarn ? cycleBarn.value : null;
      }),
    }));

    return { options: option, data };
  }

  private buildPerLocationBarnChart(
    kpi: string,
    timeSeries: DataSeries<number>,
  ): { options: Highcharts.Options; data: SeriesColumnOptions[] } {
    const option = this.createChartOptions(kpi, timeSeries);
    const valuesPerBarn: { name: string; value: number }[] = [];

    this.getKpiSeries(timeSeries, kpi).forEach((seriesItem) => {
      if (seriesItem.properties?.LocationId === undefined || !seriesItem.properties?.BarnId) {
        return;
      }

      const barn = this.getPropertyString(seriesItem.properties.BarnName);
      const location = this.getPropertyString(seriesItem.properties.LocationName);
      const barnName = `${barn} (${location})`;
      (option.xAxis as XAxisOptions).categories?.push(barnName);

      if (seriesItem.name !== 'Total' && seriesItem.values[0].value.toString() !== 'NaN') {
        valuesPerBarn.push({
          name: barnName,
          value: seriesItem.values[0].value,
        });
      }
    });

    return {
      options: option,
      data: this.createSeriesByName(valuesPerBarn),
    };
  }

  private createChartOptions(
    kpi: string,
    timeSeries: DataSeries<number>,
    disableLegend = false,
  ): Highcharts.Options {
    const option: Highcharts.Options = {
      ...this.baseOptions,
      yAxis: {
        plotLines: [],
        title: {
          text: '',
        },
      },
      xAxis: {
        categories: [],
      },
      ...(disableLegend ? { legend: { enabled: false } } : {}),
    };

    this.addBenchmarks(kpi, option);
    const metaData = this.getMetaDataForKpi(timeSeries, kpi);
    option.tooltip = this.getToolTip(metaData);

    return option;
  }

  private getMetaDataForKpi(timeSeries: DataSeries<number>, kpi: string): DataSeriesParameter {
    return timeSeries.metaData.parameters.find((parameter) => parameter.code === kpi);
  }

  private getKpiSeries(timeSeries: DataSeries<number>, kpi: string) {
    return Object.keys(timeSeries.series)
      .filter((seriesKey) => seriesKey === kpi)
      .flatMap((seriesKey) => timeSeries.series[seriesKey]);
  }

  private createSeriesByName(values: { name: string; value: number }[]): SeriesColumnOptions[] {
    const distinctNames = values.reduce<string[]>((names, value) => {
      if (!names.includes(value.name)) {
        names.push(value.name);
      }
      return names;
    }, []);

    return distinctNames.map((name) => ({
      type: 'column',
      name,
      data: values.map((value) => (value.name === name ? value.value : null)),
    }));
  }

  private getPropertyString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  getToolTip(metaData: DataSeriesParameter): TooltipOptions {
    const component = this;
    const prefix = metaData.properties['PrefixUnit'] as unknown as boolean;

    return {
      // prettier-ignore

      formatter: function() {
            return (
                !prefix ? component.decimalPipe.transform(this.y, '1.0-' + metaData.numberOfDecimals) + metaData.unit
                        : metaData.unit + component.decimalPipe.transform(this.y, '1.0-' + metaData.numberOfDecimals)
            );
          },
    } as TooltipOptions;
  }

  baseOptions = {
    chart: {
      type: 'column',
      height: '600px',
      zoomType: 'xy',
      panning: {
        enabled: true,
        type: 'xy',
      },
      panKey: 'shift',
    },
    xAxis: {
      visible: false,
    },
    yAxis: {
      visible: true,
    },
    title: {
      text: '',
    },
    colors: [
      '#109618',
      '#FF9900',
      '#3366CC',
      '#DC3912',
      '#7C0068',
      '#DBC62C',
      '#2CDBD7',
      '#FCA7FF',
    ],
    tooltip: {
      // prettier-ignore

      formatter: function() {
        return (

          this.series.name +
          ': ' +

          this.y
        );
      },
    },
    credits: {
      enabled: true,
      text: 'Agrifirm',
      href: 'https://www.agrifirm.com',
    },
  } as Highcharts.Options;

  getOptions(kpi: string) {
    const options = this.options.find((x) => x.key === kpi);
    if (options === undefined) {
      return;
    }
    return options.data;
  }

  getData(kpi: string) {
    const data = this.data.find((x) => x.key === kpi);
    if (data?.data === undefined || data.data.length === 0) {
      return;
    }
    return data.data;
  }

  getBenchmarkLastWeeks(kpi: string): number {
    const benchmark = this.benchmarksLastWeeks.find((benchmark) => benchmark.key === kpi);
    if (benchmark === undefined) {
      return;
    }

    return benchmark.data;
  }

  getBenchmarkSelectedPeriod(kpi: string): number {
    const benchmark = this.benchmarksSelectedPeriod.find((benchmark) => benchmark.key === kpi);
    if (benchmark === undefined) {
      return;
    }

    return benchmark.data;
  }

  getbenchmarkCustomerAveragSelectedPeriodData(kpi: string): number {
    const benchmark = this.benchmarkCustomerAveragSelectedPeriodData.find(
      (benchmark) => benchmark.key === kpi,
    );
    if (benchmark === undefined) {
      return;
    }

    return benchmark.data;
  }

  addBenchmarks(kpi: string, option: Options): void {
    const benchmarkLastWeeksPerLocation = this.getBenchmarkLastWeeks(kpi);
    const benchmarkSelectedPeriodPerLocation = this.getBenchmarkSelectedPeriod(kpi);
    const benchmarkCustomerAveragSelectedPeriodData =
      this.getbenchmarkCustomerAveragSelectedPeriodData(kpi);

    const possibleColors = ['#000000', '#150050', '#610094'];
    let currentColorIndex = 0;

    if (
      benchmarkLastWeeksPerLocation !== undefined &&
      benchmarkLastWeeksPerLocation.toString() !== 'NaN'
    ) {
      (option.yAxis as YAxisOptions).plotLines?.push({
        color: possibleColors[currentColorIndex++],
        value: benchmarkLastWeeksPerLocation,
        width: 2,
        dashStyle: 'Dash',
        label: {
          text:
            this.tenantTranslation.translate('farmer-insights.benchmark-title') +
            ' ' +
            this.locale.translate('farmer-insights.benchmark-last-weeks') +
            ' (' +
            this.decimalPipe.transform(benchmarkLastWeeksPerLocation) +
            ')',
        },
        zIndex: 10,
      } as YAxisPlotLinesOptions);
    }

    if (
      benchmarkSelectedPeriodPerLocation !== undefined &&
      benchmarkSelectedPeriodPerLocation.toString() !== 'NaN'
    ) {
      (option.yAxis as YAxisOptions).plotLines?.push({
        color: possibleColors[currentColorIndex++],
        value: benchmarkSelectedPeriodPerLocation,
        width: 2,
        dashStyle: 'Dash',
        label: {
          text:
            this.tenantTranslation.translate('farmer-insights.benchmark-title') +
            ' ' +
            this.locale.translate('farmer-insights.benchmark-selected-period') +
            ' (' +
            this.decimalPipe.transform(benchmarkSelectedPeriodPerLocation) +
            ')',
        },
        zIndex: 10,
      } as YAxisPlotLinesOptions);
    }

    if (
      benchmarkCustomerAveragSelectedPeriodData !== undefined &&
      benchmarkCustomerAveragSelectedPeriodData.toString() !== 'NaN'
    ) {
      (option.yAxis as YAxisOptions).plotLines?.push({
        color: possibleColors[currentColorIndex],
        value: benchmarkCustomerAveragSelectedPeriodData,
        width: 2,
        dashStyle: 'Dash',
        label: {
          text:
            this.locale.translate('farmer-insights.customer-average-selected-period') +
            ' (' +
            this.decimalPipe.transform(benchmarkCustomerAveragSelectedPeriodData) +
            ')',
        },
        zIndex: 10,
      } as YAxisPlotLinesOptions);
    }
  }

  private stringifyProperty(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }
}
